import React from 'react';
import Icon from './AppIcon';
import { getAuthHeaders } from '../lib/api';
import { BILLING_SUMMARY, PASS_THROUGH_COSTS } from '../lib/billing-copy';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'text-green-600', bg: 'bg-green-50 border-green-200', icon: 'CheckCircle' },
  trialing: { label: 'Trial', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: 'Clock' },
  past_due: { label: 'Past Due', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200', icon: 'AlertTriangle' },
  canceled: { label: 'Canceled', color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: 'XCircle' },
  incomplete: { label: 'Incomplete', color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', icon: 'AlertCircle' },
};

export default function BillingTab({ userProfile, billingLoading, setBillingLoading, billingMessage, setBillingMessage }) {
  const subStatus = userProfile?.subscription_status;
  const hasActive = subStatus === 'active' || subStatus === 'trialing';
  const statusCfg = STATUS_CONFIG[subStatus] || null;

  async function handleSubscribe() {
    setBillingLoading(true);
    setBillingMessage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/billing/create-checkout', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (err) {
      setBillingMessage({ type: 'error', text: err.message });
      setBillingLoading(false);
    }
  }

  async function handleManageBilling() {
    setBillingLoading(true);
    setBillingMessage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to open billing portal');
      window.location.href = data.url;
    } catch (err) {
      setBillingMessage({ type: 'error', text: err.message });
      setBillingLoading(false);
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      {/* Current plan card */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-1">Nurture Buddy Plan</h3>
        <p className="text-sm text-muted-foreground mb-3">{BILLING_SUMMARY}</p>
        <ul className="space-y-1.5 text-xs text-muted-foreground mb-5">
          {PASS_THROUGH_COSTS.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="text-muted-foreground/70 mt-0.5">•</span>
              {item}
            </li>
          ))}
        </ul>

        {/* Status badge */}
        {statusCfg ? (
          <div className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full border ${statusCfg.bg} ${statusCfg.color} mb-5`}>
            <Icon name={statusCfg.icon} size={15} />
            {statusCfg.label}
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full border bg-muted border-border text-muted-foreground mb-5">
            <Icon name="MinusCircle" size={15} />
            No active subscription
          </div>
        )}

        {/* Period end */}
        {userProfile?.subscription_period_end && hasActive && (
          <p className="text-xs text-muted-foreground mb-5">
            Next billing date:{' '}
            {new Date(userProfile.subscription_period_end).toLocaleDateString(undefined, {
              year: 'numeric', month: 'long', day: 'numeric',
            })}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {!hasActive && (
            <button
              onClick={handleSubscribe}
              disabled={billingLoading}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {billingLoading ? (
                <Icon name="Loader2" size={15} className="animate-spin" />
              ) : (
                <Icon name="CreditCard" size={15} />
              )}
              {billingLoading ? 'Loading…' : 'Subscribe Now'}
            </button>
          )}

          {hasActive && (
            <button
              onClick={handleManageBilling}
              disabled={billingLoading}
              className="inline-flex items-center gap-2 border border-border bg-background hover:bg-muted px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {billingLoading ? (
                <Icon name="Loader2" size={15} className="animate-spin" />
              ) : (
                <Icon name="Settings" size={15} />
              )}
              {billingLoading ? 'Loading…' : 'Manage Billing'}
            </button>
          )}
        </div>

        {billingMessage && (
          <div className={`flex items-center gap-2 mt-4 text-sm ${billingMessage.type === 'error' ? 'text-destructive' : 'text-green-600'}`}>
            <Icon name={billingMessage.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={15} />
            {billingMessage.text}
          </div>
        )}
      </div>

      {/* What's included */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-sm font-semibold text-foreground mb-3">What's included</h4>
        <ul className="space-y-2.5 text-sm text-muted-foreground">
          {[
            'Unlimited AI-generated evergreen touchpoints',
            'Automated drip SMS delivery',
            'A2P 10DLC compliance registration',
            'Local area code number matching',
            'Metered SMS usage billed by segment',
          ].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Icon name="Check" size={14} className="text-green-500 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
